import React, { useState } from 'react'

/* Atoms */
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Switch from 'components_new/atoms/Switch'
import Tooltip from 'components_new/atoms/Tooltip'

/* Molecules */
import { Option } from 'components_new/molecules/SelectSearch'
import SettingsEditDialog from 'components_new/molecules/SettingsEditDialog'
import { TInputType } from 'components_new/molecules/SettingsEditDialog/utils'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'

/* Actions */
import { useInternalCustomerActions } from 'redux/actions/internal/Customers'

/* Page components */
import NormalizedRentMappingDialog from './NormalizedRentMappingDialog'
import TestEnvironmentInfoDialog from './TestEnvironmentInfoDialog'
import TransferModelDialog from './TransferModelDialog'

const OBJECTS_FIELD_OPTIONS: Option[] = [
  { id: 'size', title: 'Objektspecifikation' },
  { id: 'category', title: 'Kategori' },
  { id: 'type', title: 'Typ' },
  { id: 'subtype', title: 'Undertyp' }
]

/* Types */
import {
  MaintenanceRequestSource,
  translateMaintenanceRequestSource,
  BudgetMonth,
  Country,
  FrequencyOfDataSynchronization,
  InternalCustomer,
  License,
  PatchInternalCustomerBody,
  Segment,
  SupportLevel,
  translateBudgetMonth,
  translateCountry,
  translateFrequencyOfDataSynchronization,
  translateLicense,
  translateSegment,
  translateSupportLevel
} from 'types/GlobalCustomer'

interface RightSidebarProps {
  customer: InternalCustomer
}

export const RightSidebar = (props: RightSidebarProps) => {
  const { customer } = props

  const {
    tryUpdateCustomer,
    tryToggleTestEnvironment,
    tryTransferTestEnvironmentToProduction
  } = useInternalCustomerActions()

  const [mappingDialogOpen, setMappingDialogOpen] = useState(false)
  const [testEnvInfoOpen, setTestEnvInfoOpen] = useState(false)
  const [testEnvToggling, setTestEnvToggling] = useState(false)
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  const [transferring, setTransferring] = useState(false)

  const [dialog, setDialog] = useState<{
    open: boolean
    title: string
    type: TInputType
    options: Option[]
    data: {
      property:
        | 'segment'
        | 'country'
        | 'license'
        | 'allowed_historical_time_period'
        | 'frequency_of_data_synchronization'
        | 'number_of_dashboards'
        | 'support_level'
        | 'budget_month'
        | 'link_to_drive'
        | 'billed_sqm'
        | 'object_size_field'
        | 'maintenance_request_source'
      value: string | null
    }
    clearValue?: string
    required?: boolean
    maxNumber?: number
  }>({
    open: false,
    title: '',
    type: 'select',
    data: { property: 'segment', value: '' },
    options: []
  })

  if (!customer) return null

  /*-- dialog --*/
  const handleOpenDialog = (
    property:
      | 'segment'
      | 'country'
      | 'license'
      | 'allowed_historical_time_period'
      | 'frequency_of_data_synchronization'
      | 'number_of_dashboards'
      | 'support_level'
      | 'budget_month'
      | 'link_to_drive'
      | 'billed_sqm'
      | 'object_size_field'
      | 'maintenance_request_source'
  ) => {
    let dialogTitle = 'Segment'
    let value: string | null = customer.meta.segment
    let options: Option[] = []
    let type: TInputType = 'select'
    let clearValue: string | undefined
    let required: boolean | undefined
    let maxNumber: number | undefined

    if (property === 'segment') {
      options = Object.values(Segment).map((option) => ({
        title: translateSegment[option].display,
        id: option
      }))
    } else if (property === 'country') {
      dialogTitle = 'Land'
      value = customer.meta.country
      options = Object.values(Country).map((option) => ({
        title: translateCountry[option].display,
        id: option
      }))
    } else if (property === 'maintenance_request_source') {
      dialogTitle = 'Felanmälningar registreras som'
      value = customer.maintenance_request_source
      options = Object.values(MaintenanceRequestSource).map((option) => ({
        title: translateMaintenanceRequestSource[option].display,
        id: option
      }))
    } else if (property === 'license') {
      dialogTitle = 'Licens'
      value = customer.license
      options = Object.values(License).map((option) => ({
        title: translateLicense[option].display,
        id: option
      }))
    } else if (property === 'allowed_historical_time_period') {
      dialogTitle = 'Historisk period'
      value = customer.allow_access.allowed_historical_time_period.toString()
      type = 'number'
      clearValue = '3'
      required = true
      maxNumber = 5
    } else if (property === 'frequency_of_data_synchronization') {
      dialogTitle = 'Inläsningsfrekvens'
      value = customer.allow_access.frequency_of_data_synchronization
      options = Object.values(FrequencyOfDataSynchronization).map((option) => ({
        title: translateFrequencyOfDataSynchronization[option].display,
        id: option
      }))
    } else if (property === 'number_of_dashboards') {
      dialogTitle = 'Antal dashboards'
      value = customer.number_of_dashboards
        ? customer.number_of_dashboards.toString()
        : null
      type = 'number'
    } else if (property === 'support_level') {
      dialogTitle = 'Supportnivå'
      value = customer.allow_access.support_level
      options = Object.values(SupportLevel).map((option) => ({
        title: translateSupportLevel[option].display,
        id: option
      }))
    } else if (property === 'budget_month') {
      dialogTitle = 'Budgetmånad'
      value = customer.budget_month
      options = Object.values(BudgetMonth).map((option) => ({
        title: translateBudgetMonth[option].display,
        id: option
      }))
    } else if (property === 'link_to_drive') {
      dialogTitle = 'Länk till Drive'
      value = customer.link_to_drive
      type = 'text'
    } else if (property === 'billed_sqm') {
      dialogTitle = 'Kvm som faktureras'
      value =
        customer.billed_sqm !== null ? customer.billed_sqm.toString() : 'Saknas'
      type = 'number'
    } else if (property === 'object_size_field') {
      dialogTitle = 'Storleksfält'
      value = customer.object_size_field
      options = OBJECTS_FIELD_OPTIONS
    }

    setDialog({
      open: true,
      title: dialogTitle,
      type,
      data: { property, value },
      options,
      clearValue,
      required,
      maxNumber
    })
  }

  const handleCloseDialog = () => {
    setDialog((prevState) => ({ ...prevState, open: false }))
  }

  const handleEditDialogValue = (value: string) => {
    setDialog((prevState) => ({
      ...prevState,
      data: { ...prevState.data, value }
    }))
  }

  const handleSubmitDialog = () => {
    tryUpdateCustomer(customer.id, {
      data: {
        [dialog.data.property]:
          dialog.data.property === 'number_of_dashboards' && !dialog.data.value
            ? null
            : dialog.data.value
      }
    } as PatchInternalCustomerBody)

    handleCloseDialog()
  }

  return (
    <>
      <SettingsGroup
        title={'Meta'}
        helperText={
          'Intern klassificering av kund för statistik och uppföljning'
        }
      >
        <SettingsItem
          onClick={() => handleOpenDialog('segment')}
          title={'Segment'}
          value={
            !customer.meta.segment
              ? null
              : translateSegment[customer.meta.segment].display
          }
          variant="edit"
        />
        <SettingsItem
          onClick={() => handleOpenDialog('country')}
          title={'Land'}
          value={
            !customer.meta.country
              ? null
              : translateCountry[customer.meta.country].display
          }
          variant="edit"
        />
        <SettingsItem
          onClick={() => handleOpenDialog('license')}
          title={'Licens'}
          value={
            !customer.license
              ? null
              : translateLicense[customer.license].display
          }
          variant="edit"
        />
        <SettingsItem
          onClick={() => handleOpenDialog('support_level')}
          title={'Supportnivå'}
          value={
            !customer.allow_access.support_level
              ? null
              : translateSupportLevel[customer.allow_access.support_level]
                .display
          }
          variant="edit"
        />
        <SettingsItem
          onClick={() => handleOpenDialog('budget_month')}
          title={'Budgetmånad'}
          value={
            !customer.budget_month
              ? null
              : translateBudgetMonth[customer.budget_month].display
          }
          variant="edit"
        />
        <SettingsItem
          onClick={() => handleOpenDialog('link_to_drive')}
          title={'Länk till Drive'}
          value={customer.link_to_drive}
          variant="edit"
        />
        <SettingsItem
          onClick={() => handleOpenDialog('billed_sqm')}
          title={'Kvm som faktureras'}
          value={
            customer.billed_sqm !== null
              ? customer.billed_sqm.toString()
              : 'Saknas'
          }
          variant="edit"
        />
      </SettingsGroup>
      <SettingsGroup
        title={'Inställningar'}
        helperText={
          'Generella inställningar som påverkar kundens hela plattform'
        }
      >
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.is_demo}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: { is_demo: !customer.is_demo }
                })
              }
              size="small"
            />
          }
          title={'Demokonto'}
        />
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_financial_year}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: { allow_financial_year: !customer.allow_financial_year }
                })
              }
              size="small"
            />
          }
          title={'Verksamhetsår'}
        />
        <SettingsItem
          actions={
            <Switch
              checked={customer.allow_access.allow_currency_converter}
              color="success"
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    allow_currency_converter:
                      !customer.allow_access.allow_currency_converter
                  }
                })
              }
              size="small"
            />
          }
          title={'Valutaomvandlare'}
        />
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_access.sso}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    sso: !customer.allow_access.sso
                  }
                })
              }
              size="small"
            />
          }
          title={'Microsoft SSO'}
        />
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_access.access_hub}
              disabled={!customer.allow_access.access_hub}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: { access_hub: false }
                })
              }
              size="small"
            />
          }
          body={'Tjänsten är utfasad och kan endast avaktiveras.'}
          title={'Access Hub'}
        />
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.using_homepal_star_schema}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    using_homepal_star_schema:
                      !customer.using_homepal_star_schema
                  }
                })
              }
              size="small"
            />
          }
          title={'Homepal interna mätetal'}
        />
        <SettingsItem
          onClick={() => handleOpenDialog('allowed_historical_time_period')}
          title={'Historisk period'}
          value={`Innevarande år + ${customer.allow_access.allowed_historical_time_period} år`}
          variant="edit"
        />
        <SettingsItem
          onClick={() => handleOpenDialog('frequency_of_data_synchronization')}
          title={'Inläsningsfrekvens'}
          value={
            !customer.allow_access.frequency_of_data_synchronization
              ? null
              : translateFrequencyOfDataSynchronization[
                customer.allow_access.frequency_of_data_synchronization
              ].display
          }
          variant="edit"
        />
      </SettingsGroup>

      <SettingsGroup
        title={'Intern uppföljning'}
        helperText={
          'För kunder som vill följa upp sin verksamhet. Aktiverar möjlighet att se hela beståndet, dela ut specifik dataåtkomst och att se mina fastigheter.'
        }
      >
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_access.internal_follow_up}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    internal_follow_up:
                      !customer.allow_access.internal_follow_up
                  }
                })
              }
              size="small"
            />
          }
          title={'Aktiverat'}
        />
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.organizational_unit_access_type === 'company'}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    organizational_unit_access_type:
                      customer.organizational_unit_access_type === 'company'
                        ? 'real_estate'
                        : 'company'
                  }
                })
              }
              size="small"
            />
          }
          title={'Bolagsbaserad dataåtkomst'}
        />
      </SettingsGroup>

      <SettingsGroup
        title={'Delning till kund'}
        helperText={
          'För kunder som vill dela data till sina kunder. Aktiverar möjlighet att bjuda in användare till specifika delar av beståndet som inte tillhör den egna organisationen (fd. förvaltningsbolag).'
        }
      >
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.is_management_company}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    is_management_company: !customer.is_management_company,
                    integrate_company_groups: false
                  }
                })
              }
              size="small"
            />
          }
          title={'Aktiverat'}
        />
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.integrate_company_groups}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    is_management_company: true,
                    integrate_company_groups: !customer.integrate_company_groups
                  }
                })
              }
              size="small"
            />
          }
          title={'Kunduppdelning via SIM'}
        />
      </SettingsGroup>

      <SettingsGroup
        title={'Dashboards'}
        helperText={'Dashboardtjänsten, antal och anpassningsmöjligheter'}
      >
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={true}
              onChange={() => {}}
              size="small"
              disabled
            />
          }
          title={'Aktiverat'}
        />
        <SettingsItem
          onClick={() => handleOpenDialog('number_of_dashboards')}
          title={'Antal'}
          value={customer.number_of_dashboards}
          variant="edit"
        />
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_access.embed}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    embed: !customer.allow_access.embed
                  }
                })
              }
              size="small"
            />
          }
          title={'Inbäddning'}
        />
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_access.white_label_colors}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    white_label_colors:
                      !customer.allow_access.white_label_colors
                  }
                })
              }
              size="small"
            />
          }
          title={'Färgsättning'}
        />
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_access.white_label_logotype}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    white_label_logotype:
                      !customer.allow_access.white_label_logotype
                  }
                })
              }
              size="small"
            />
          }
          title={'Logotyp'}
        />
      </SettingsGroup>

      <SettingsGroup
        title={'Rapporter'}
        helperText={'Åtkomst till rapportfunktionen'}
      >
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_reports}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: { allow_reports: !customer.allow_reports }
                })
              }
              size="small"
            />
          }
          title={'Aktiverat'}
        />
      </SettingsGroup>

      <SettingsGroup
        title={'Nyckeltal'}
        helperText={'Åtkomst till nyckeltaltjänsten'}
      >
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_key_metrics}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: { allow_key_metrics: !customer.allow_key_metrics }
                })
              }
              size="small"
            />
          }
          title={'Aktiverat'}
        />
      </SettingsGroup>

      <SettingsGroup
        title={'MCP'}
        helperText={
          'Åtkomst till MCP-integrationen (anslut Claude, ChatGPT och Microsoft Copilot)'
        }
      >
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_mcp}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: { allow_mcp: !customer.allow_mcp }
                })
              }
              size="small"
            />
          }
          title={'Aktiverat'}
        />
      </SettingsGroup>

      <SettingsGroup
        title={'Insikter'}
        helperText={'Åtkomst till insiktstjänsten'}
      >
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_analyses}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: { allow_analyses: !customer.allow_analyses }
                })
              }
              size="small"
            />
          }
          title={'Aktiverat'}
        />
        <SettingsItem
          onClick={() => handleOpenDialog('maintenance_request_source')}
          title={'Felanmälningar registreras som'}
          value={
            customer.maintenance_request_source
              ? translateMaintenanceRequestSource[
                customer.maintenance_request_source
              ].display
              : null
          }
          variant="edit"
        />
      </SettingsGroup>

      <SettingsGroup
        title={'AI-chatt (visa innehåll)'}
        helperText={'Åtkomst till AI-chatt för visa innehåll'}
      >
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_ai_content_chat}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    allow_ai_content_chat: !customer.allow_ai_content_chat
                  }
                })
              }
              size="small"
            />
          }
          title={'Aktiverat'}
        />
      </SettingsGroup>

      <SettingsGroup
        title={'Tabeller'}
        helperText={'Åtkomst till tabelltjänsten'}
      >
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_access.export_tables}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    export_tables: !customer.allow_access.export_tables
                  }
                })
              }
              size="small"
            />
          }
          title={'Aktiverat'}
        />
      </SettingsGroup>

      <SettingsGroup
        title={'AI-kategorisering av ärenden'}
        helperText={'AI-driven databerikning för ärendeklassificering'}
      >
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_errand_ai_safety_measures}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    allow_errand_ai_safety_measures:
                      !customer.allow_errand_ai_safety_measures
                  }
                })
              }
              size="small"
            />
          }
          title={'Trygghet'}
        />
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_errand_ai_error_source}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    allow_errand_ai_error_source:
                      !customer.allow_errand_ai_error_source
                  }
                })
              }
              size="small"
            />
          }
          title={'Fel'}
        />
      </SettingsGroup>

      <SettingsGroup
        title={'AI-kategorisering av arbetsordrar'}
        helperText={
          'AI-driven databerikning för klassificering av arbetsordrar'
        }
      >
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_ticket_ai_safety_measures}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    allow_ticket_ai_safety_measures:
                      !customer.allow_ticket_ai_safety_measures
                  }
                })
              }
              size="small"
            />
          }
          title={'Trygghet'}
        />
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_ticket_ai_error_source}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    allow_ticket_ai_error_source:
                      !customer.allow_ticket_ai_error_source
                  }
                })
              }
              size="small"
            />
          }
          title={'Fel'}
        />
      </SettingsGroup>

      <SettingsGroup
        title={'Berikning'}
        helperText={
          'Inställningar för databerikning och standardisering av lägenhetsdata'
        }
      >
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.allow_normalized_rent}
              onChange={() =>
                tryUpdateCustomer(customer.id, {
                  data: {
                    allow_normalized_rent: !customer.allow_normalized_rent
                  }
                })
              }
              size="small"
            />
          }
          title={'Normhyra'}
        />
        <SettingsItem
          onClick={() => handleOpenDialog('object_size_field')}
          title={'Storleksfält'}
          value={
            customer.object_size_field
              ? OBJECTS_FIELD_OPTIONS.find(
                (o) => o.id === customer.object_size_field
              )?.title ?? customer.object_size_field
              : null
          }
          variant="edit"
        />
        <SettingsItem
          onClick={() => setMappingDialogOpen(true)}
          title={'Mappningar'}
          variant="edit"
        />
      </SettingsGroup>

      <SettingsGroup
        title={'Testmiljö'}
        helperText={
          'Parallell informationsmodell för validering av systembyten. Endast synlig för Homepal-användare.'
        }
        actions={
          <Tooltip placement="left" title="Så fungerar testmiljön">
            <IconButton
              onClick={() => setTestEnvInfoOpen(true)}
              size="small"
              sx={{ p: 0.25 }}
            >
              <Icon color="disabled" fontSize="small" name="Info" />
            </IconButton>
          </Tooltip>
        }
      >
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={customer.test_environment_enabled}
              disabled={testEnvToggling}
              onChange={() => {
                setTestEnvToggling(true)

                Promise.resolve(
                  tryToggleTestEnvironment(
                    customer.id,
                    !customer.test_environment_enabled
                  )
                ).finally(() => setTestEnvToggling(false))
              }}
              size="small"
            />
          }
          title={'Aktiverad'}
        />
        {customer.test_environment_enabled ? (
          <SettingsItem
            onClick={() => setTransferDialogOpen(true)}
            title={'Överför modellering till produktion'}
            variant="edit"
          />
        ) : null}
      </SettingsGroup>

      <NormalizedRentMappingDialog
        customerId={customer.id}
        open={mappingDialogOpen}
        onClose={() => setMappingDialogOpen(false)}
      />

      <TestEnvironmentInfoDialog
        open={testEnvInfoOpen}
        onClose={() => setTestEnvInfoOpen(false)}
      />

      <TransferModelDialog
        open={transferDialogOpen}
        loading={transferring}
        onClose={() => setTransferDialogOpen(false)}
        onConfirm={() => {
          setTransferring(true)

          Promise.resolve(
            tryTransferTestEnvironmentToProduction(customer.id)
          ).finally(() => {
            setTransferring(false)
            setTransferDialogOpen(false)
          })
        }}
      />

      <SettingsEditDialog
        onChange={handleEditDialogValue}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitDialog}
        open={dialog.open}
        title={dialog.title}
        type={dialog.type}
        value={dialog.data.value ?? ''}
        options={dialog.options}
        clearValue={dialog.clearValue}
        required={dialog.required}
        maxNumber={dialog.maxNumber}
      />
    </>
  )
}
